import { type FC } from "react";

import { cn } from "@/components/podkit/lib/cn";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { IconPlusLarge } from "central-icons/IconPlusLarge";
import { Tooltip } from "@/components/Tooltip";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

import { CreateEnvironmentModalKeyBinding } from "@/utils/platform";
import { Button } from "@/components/flexkit/Button";

type CreateSessionButtonProps = {
    isLocked: boolean;
    onClick?: () => void;
};

export const NewEnvironmentButton: FC<CreateSessionButtonProps> = ({ isLocked, onClick }) => {
    const button = (
        <Button
            variant="outline"
            data-testid="create-session-button"
            aria-label="New Environment"
            onClick={onClick}
            className={cn("relative gap-0 px-0 py-0", {
                "h-8 w-8": !isLocked,
                "h-8 w-full": isLocked,
            })}
            data-tracking-id="new-environment"
        >
            <CreateSessionIcon />
            <CreateSessionText isLocked={isLocked} />
        </Button>
    );

    if (!isLocked) {
        return (
            <Tooltip content="New Environment" side="right" usePortal>
                {button}
            </Tooltip>
        );
    }

    return button;
};

type CreateSessionComponentProps = {
    isLocked: boolean;
};

const CreateSessionText: FC<CreateSessionComponentProps> = ({ isLocked }) => {
    const { isMobileViewport } = useIsMobileViewport();

    return (
        <div
            className={cn(
                "absolute right-0 flex items-center justify-between overflow-hidden pr-2",
                isLocked ? "left-8 opacity-100" : "left-8 opacity-0",
            )}
        >
            <span data-testid="session-text" className="whitespace-nowrap">
                New Environment
            </span>
            {!isMobileViewport && (
                <div data-testid="keyboard-shortcut">
                    <KeyboardCombo
                        keys={CreateEnvironmentModalKeyBinding}
                        className="bg-surface-muted text-content-muted"
                    />
                </div>
            )}
        </div>
    );
};

const CreateSessionIcon: FC = (): JSX.Element => {
    return (
        <div
            data-testid="create-session-icon"
            className="absolute -left-px flex h-8 w-8 shrink-0 items-center justify-center"
        >
            <IconPlusLarge size={16} />
        </div>
    );
};
